import { Dispatch, SetStateAction, useCallback } from 'react';
import { PROSPECT_TYPE_OPTIONS, PUBLIC_PROSPECT_CATEGORY_OPTIONS } from '../../../constants';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { setCategory, setType } from '../../../redux/reducers';
import { Permissions, ProspectCategory, ProspectType } from '../../../types';
import { ButtonColor, ButtonVariant, Select, TextButton, Typography, TypographyType } from '../../shared';

const MAX_SCENARIO_OPTIONS_HEIGHT = 320;

interface CreateCustomSimulationProps {
  setNewCategory: Dispatch<SetStateAction<ProspectCategory | undefined>>;
  setNewType: Dispatch<SetStateAction<ProspectType | undefined>>;
  goToPrevStep: () => void;
  filterProspectCategories: (category: ProspectCategory) => boolean;
  isAITwin?: boolean;
  newCategory?: ProspectCategory;
  newType?: ProspectType;
  hideNextButton?: boolean;
}

const CreateCustomSimulation = ({
  setNewCategory,
  setNewType,
  goToPrevStep,
  filterProspectCategories,
  isAITwin,
  newCategory,
  newType,
  hideNextButton,
}: CreateCustomSimulationProps) => {
  const dispatch = useAppDispatch();

  const { user, organization } = useAppSelector((state) => state.auth);

  const isSuperAdmin = (user?.permissions || []).includes(Permissions.ADMIN_ACCESS);
  const isCustomSimsEnabled = !!organization?.isCustomSimsEnabled;

  const categoryOptions = PUBLIC_PROSPECT_CATEGORY_OPTIONS.filter(({ value }) => filterProspectCategories(value));
  const typeOptions = isAITwin
    ? PROSPECT_TYPE_OPTIONS.filter((option) => ProspectType.CUSTOM !== option.value)
    : PROSPECT_TYPE_OPTIONS.map((option) => ({
        disabled: ProspectType.CUSTOM === option.value && !isCustomSimsEnabled && !isSuperAdmin,
        ...option,
      }));

  const onNext = useCallback(() => {
    if (!newCategory || !newType) return;

    dispatch(setCategory(newCategory));
    dispatch(setType(newType));
  }, [newCategory, newType, dispatch]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <Typography type={TypographyType.BASE}>How will this simulation be used?*</Typography>
          <Select
            placeholder="Select category"
            options={categoryOptions}
            selected={newCategory}
            onChange={(value) => setNewCategory(value as ProspectCategory)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Typography type={TypographyType.BASE}>What kind of scenario are you simulating?*</Typography>
          <Select
            placeholder="Select scenario"
            options={typeOptions}
            maxOptionsHeight={MAX_SCENARIO_OPTIONS_HEIGHT}
            selected={newType}
            onChange={(value) => setNewType(value as ProspectType)}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <TextButton onClick={goToPrevStep} text="Back" variant={ButtonVariant.SOFT} color={ButtonColor.NEUTRAL} />
        {!hideNextButton && <TextButton onClick={onNext} text="Next" disabled={!newType || !newCategory} />}
      </div>
    </div>
  );
};

export default CreateCustomSimulation;
